import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { LayoutGrid, X, Send, Plus, Trash2 } from 'lucide-react'
import type { OutlineChangeQueueItem } from '@/types/outlineChangeQueue'

// Unified neutral chip style
const chipStyle = {
  container: 'bg-muted/70 border border-border rounded-lg',
  title: 'text-foreground',
  desc: 'text-muted-foreground',
  remove: 'text-muted-foreground/50',
  removeHover: 'hover:bg-muted hover:text-foreground',
}

const chipIcons: Record<string, React.ReactNode> = {
  layout_change: <LayoutGrid className="h-3 w-3 shrink-0 text-muted-foreground" />,
  add_slide: <Plus className="h-3 w-3 shrink-0 text-muted-foreground" />,
  delete_slide: <Trash2 className="h-3 w-3 shrink-0 text-muted-foreground" />,
}

interface OutlineChangeQueueDisplayProps {
  queue: OutlineChangeQueueItem[]
  onRemove: (id: string) => void
  onSendAll: () => void
  isProcessing?: boolean
  className?: string
}

export const OutlineChangeQueueDisplay: React.FC<OutlineChangeQueueDisplayProps> = ({
  queue,
  onRemove,
  onSendAll,
  isProcessing = false,
  className
}) => {
  if (queue.length === 0) return null

  return (
    <div className={cn("space-y-2", className)}>
      {/* Header with count and send button */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <LayoutGrid className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {queue.length} Outline Change{queue.length !== 1 ? 's' : ''} Queued
          </span>
        </div>
        <Button
          onClick={onSendAll}
          disabled={isProcessing}
          size="sm"
          className="h-7 bg-foreground text-background hover:bg-foreground/90 text-xs"
        >
          <Send className="h-3 w-3 mr-1.5" />
          {isProcessing ? 'Sending...' : 'Send to AI'}
        </Button>
      </div>

      {/* Queue items as compact color-coded chips */}
      <div className="flex flex-wrap gap-2">
        {queue.map((item) => {
          const icon = chipIcons[item.changeType] || chipIcons.layout_change
          return (
            <div
              key={item.id}
              className={`group inline-flex items-center gap-2 px-3 py-1.5 ${chipStyle.container}`}
            >
              {icon}
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-medium truncate ${chipStyle.title}`}>
                  {item.slideTitle}
                </div>
                <div className={`text-xs truncate ${chipStyle.desc}`}>
                  {item.description}
                </div>
              </div>
              <button
                onClick={() => onRemove(item.id)}
                disabled={isProcessing}
                className={`flex-shrink-0 p-1 rounded ${chipStyle.remove} ${chipStyle.removeHover} transition-colors disabled:opacity-50`}
                aria-label={`Remove ${item.slideTitle} change`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
